import { getHealthSafetyDocumentUrl } from '@/lib/document-url';
import { useState, useEffect, useRef } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import DocumentViewerModal, { DocumentViewerModalRef } from '@/components/document-viewer-modal';

import { selectJobOverviewData } from '@/store/selectors';
import { addJobState, updateJobState, getHSDocuments, updateDocReadAt } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/health-safety-files')({
  component: () => (
    <ProtectedRoute>
      <JobHealthAndSafetyFilesPage />
    </ProtectedRoute>
  ),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      allDocsOnly: Boolean(search.allDocsOnly),
    };
  },
});

interface HSDocument {
  id: string;
  filename: string;
  tagName: string;
  last_read_at?: string;
  updated_at: string;
  [key: string]: any;
}

function JobHealthAndSafetyFilesPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/health-safety-files' });
  const { allDocsOnly } = useSearch({ from: '/job/$jobId/$date/health-safety-files' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const documentViewerRef = useRef<DocumentViewerModalRef>(null);

  // Get job data from Redux
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  // Get health and safety documents
  const hsDocuments = useSelector((state: RootState) => state.hs_documents);

  useEffect(() => {
    // Load H&S documents if not already loaded
    if (hsDocuments.items.length === 0 && !hsDocuments.loading) {
      dispatch(getHSDocuments());
    }
  }, [dispatch, hsDocuments.items.length, hsDocuments.loading]);

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date/health-safety', params: { jobId, date } });
  };

  const handleGoBackToOverview = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_health_and_safety: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_health_and_safety: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }

    // Navigate to next step based on job state
    if (jobState?.confirmed_protective_photos) {
      handleGoBackToOverview();
    } else {
      navigate({ to: '/job/$jobId/$date/photos', params: { jobId, date } });
    }
  };

  const handleOpenDocument = (document: HSDocument) => {
    if (documentViewerRef.current) {
      // Use health_safety path for HS documents
      const baseUrl = getHealthSafetyDocumentUrl(document.filename);
      documentViewerRef.current.openDocument(document, baseUrl);
    }

    // Mark document as read
    dispatch(updateDocReadAt({
      id: document.id,
      last_read_at: moment().format('YYYY-MM-DD HH:mm:ss'),
    }));
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  const getDocumentStatusText = (document: HSDocument) => {
    if (!document.last_read_at) {
      return "Never read";
    }

    const lastRead = moment(document.last_read_at);
    const updated = moment(document.updated_at);

    if (updated.isAfter(lastRead)) {
      return `Updated ${updated.fromNow()}`;
    }

    return `Last read ${lastRead.fromNow()}`;
  };

  const getDocumentStatusColor = (document: HSDocument) => {
    if (!document.last_read_at) {
      return "text-red-600";
    }

    const lastRead = moment(document.last_read_at);
    const updated = moment(document.updated_at);

    if (updated.isAfter(lastRead)) {
      return "text-amber-600";
    }

    return "text-green-600";
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight">
              {allDocsOnly
                ? "Health & Safety documents"
                : "Complete your Health & Safety requirements"}
            </h1>

            <p className="text-base leading-relaxed">
              Please ensure you're up-to-date and familiar with all the documents below.
            </p>
          </div>

          {/* Documents Section */}
          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Documents
            </Label>

            <div className="border-t border-gray-200 pt-4">
            {hsDocuments.loading ? (
              <p className="text-gray-500">Loading documents...</p>
            ) : hsDocuments.items.length > 0 ? (
                <div className="py-2">

                      {hsDocuments.items.map((document, index) => (
                        <div
                          key={`doc-${index}`}
                          className="cursor-pointer hover:bg-gray-50 py-2 transition-colors"
                          onClick={() => handleOpenDocument(document)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                              <svg
                                className="w-5 h-5 text-gray-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-black font-bold text-sm truncate">
                                {document.tagName}
                              </p>
                            </div>

                            <div className="flex-shrink-0">
                              <p className={`text-xs ${getDocumentStatusColor(document)}`}>
                                {getDocumentStatusText(document)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-gray-500">No health and safety documents available.</p>
                </CardContent>
              </Card>
            )}
          </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={allDocsOnly ? handleGoBack : handleUpdateJobState}
              className="flex-1"
            >
              {allDocsOnly ? "Go Back" : "I am compliant"}
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal ref={documentViewerRef} />
    </ScrollArea>
  );
}
